/**
 * Land and grazing: where cattle are (moves project the animal's location), how pastures are used
 * and rested, grazing plans, forage checks, rain, hay, field work and water checks. Planned moves are
 * plan steps or tasks; only moves that happened are events.
 */
import {
  UNIT_LABELS, addDays, daysBetween, defineEvent, defineFacet, defineModule, f, msg,
  type Badge, type Fact, type FacetEvent, type Formatters, type Precision, type Quantity, type SubjectRef,
} from "../../contract";
import {
  FORAGE_UNITS, WATER_SEVERITY, dayOf, rainIn, restDays, roundRain, staysFromMoves,
  type MoveLike, type RainUnit, type Stay, type WaterState,
} from "./shared/grazing";

// ---------------------------------------------------------------- shared vocab

export const MOVE_REASONS = {
  rotation: "Rotation", weaning: "Weaning", calving: "Calving", breeding: "Breeding", working: "Working cattle", doctoring: "Doctoring",
  shipping: "Shipping or sale", weather: "Weather", water: "Water", feed: "Feed or forage", starting: "Starting place", other: "Other",
} as const;
export type MoveReason = keyof typeof MOVE_REASONS;

export const RAIN_KIND = { rain: "Rain", snow: "Snow (water in the gauge)", hail: "Hail" } as const;
export const RAIN_BASIS = { gauge: "Gauge reading", estimate: "Estimate", station: "Weather station" } as const;
export const FORAGE_CONDITION = { good: "Good", fair: "Fair", poor: "Poor" } as const;
export const FORAGE_GRAZED = { none: "Not grazed", light: "Lightly grazed", half: "About half grazed", heavy: "Grazed down" } as const;
export const FORAGE_BASIS = { eyeball: "Eyeball estimate", stick: "Grazing stick or ruler", plate: "Plate meter", clipped: "Clipped and weighed", lab: "Lab or service" } as const;
export const BALE_TYPES = { round: "Round", small_square: "Small square", large_square: "Big square" } as const;
export const WEIGHT_BASIS = { estimated: "Estimated", weighed: "Weighed" } as const;
export const FIELD_WORK = {
  spray: "Spray", fertilize: "Fertilize", plant: "Plant or seed", burn: "Burn", mow: "Mow or shred", till: "Till or disk", irrigate: "Irrigate", other: "Other",
} as const;
export const WATER_STATES = { ok: "Full or OK", low: "Low", empty: "Empty", broken: "Broken or not pumping" } as const;
export const WATER_SOURCES = { tank: "Tank", trough: "Trough", windmill: "Windmill", well: "Well or pump", pond: "Pond or tank dam", creek: "Creek", other: "Other" } as const;

/** Rate units for field work: product per area. Stored as written; never converted silently. */
export const RATE_UNITS = ["qt/ac", "pt/ac", "gal/ac", "oz/ac", "lb/ac", "l/ha", "kg/ha"] as const;

// ---------------------------------------------------------------- event data

export interface MoveData {
  head?: number;
  count_unit?: "head" | "pairs";
  whole_group?: boolean;
  reason?: MoveReason;
  remaining?: number;
  note?: string;
}

export interface RainfallData {
  amount: Quantity;
  since?: string;
  kind?: keyof typeof RAIN_KIND;
  basis?: keyof typeof RAIN_BASIS;
  snow_depth?: Quantity;
  note?: string;
}

export interface ForageData {
  height?: Quantity;
  condition?: keyof typeof FORAGE_CONDITION;
  grazed?: keyof typeof FORAGE_GRAZED;
  mass?: Quantity;
  basis?: keyof typeof FORAGE_BASIS;
  species?: string;
  note?: string;
}

export interface HayHarvestData {
  bales: number;
  bale_type?: keyof typeof BALE_TYPES;
  cutting?: number;
  bale_weight?: Quantity;
  weight_basis?: keyof typeof WEIGHT_BASIS;
  moisture?: number;
  acres?: Quantity;
  forage?: string;
  stock_item_id?: string;
  own_lot?: boolean;
  lot_code?: string;
  lot_id?: string;
  quality?: { dry_matter?: number; crude_protein?: number; adf?: number; ndf?: number; rfv?: number };
  note?: string;
}

export interface FieldWorkData {
  work: keyof typeof FIELD_WORK;
  product_id?: string;
  rate?: Quantity;
  used?: Quantity;
  acres?: Quantity;
  target?: string;
  keep_off_days?: number;
  done_by?: string;
  note?: string;
}

export interface WaterCheckData {
  state: WaterState;
  source?: keyof typeof WATER_SOURCES;
  level?: number;
  note?: string;
}

// ---------------------------------------------------------------- facet state (published for other modules)

/** Animal facet `location`. Other modules rely on `place_id` and `since`. */
export interface LocationState {
  place_id: string;
  since: string;
  precision: Precision;
  event_id: string;
  from_place_id?: string;
  group_id?: string;
  moves: number;
  /** Most recent stays, oldest first. The full ledger is on the server. */
  history: Stay[];
}

export interface PlaceLandState {
  rain?: { unit: RainUnit; months: Record<string, number>; last: { on: string; amount: number; since?: string }; readings: number };
  forage?: { on: string; event_id: string; height?: Quantity; condition?: string; grazed?: string; mass?: Quantity; basis?: string };
  hay?: { years: Record<string, { bales: number; cuttings: number }>; last: { on: string; bales: number; bale_type?: string; cutting?: number; event_id: string } };
  work?: { last: { on: string; work: string; event_id: string }; off_until?: string };
  water?: { on: string; state: WaterState; worst: WaterState; sources: Record<string, { on: string; state: WaterState }> };
  /** Cattle moved in or out by count only, without named animals. */
  counted?: { head: number; unit: "head" | "pairs"; since: string };
}

/**
 * Place facet `grazing`: occupancy and rest from the stays ledger. The land server rewrites it after
 * every move, correction or void (the engine cannot, because a move's origin place is often unsaid).
 */
export interface GrazingState {
  head: number;
  /** Pastures, traps and fields rest between grazings; pens and facilities don't. */
  grazed: boolean;
  occupied_since?: string;
  empty_since?: string;
  rest_target?: number;
  capacity?: number;
  episodes: Array<{ from: string; to: string | null }>;
  computed_on: string;
}

export interface AssetLandState { water: { on: string; state: WaterState; level?: number } }

// ---------------------------------------------------------------- helpers for summaries and facets

const role = (subjects: SubjectRef[], r: string) => subjects.find((s) => s.role === r);
const lab = (x: Formatters, s: SubjectRef | undefined) => (s ? s.label ?? x.label(s.id) : "");

function moveWho(d: MoveData, subjects: SubjectRef[], x: Formatters): string {
  const animals = subjects.filter((s) => s.role === "animal");
  const group = role(subjects, "group");
  const n = animals.length;
  if (!n) {
    const count = d.head ? (d.count_unit === "pairs" ? x.count(d.head, "{{count}} pair", "{{count}} pairs") : x.count(d.head, "{{count}} head", "{{count}} head")) : x.t("Cattle");
    return group ? x.t("{{group}} ({{head}}, counted)", { group: lab(x, group), head: count }) : x.t("{{head}}, counted", { head: count });
  }
  if (group) {
    return d.whole_group
      ? x.t("{{group}} ({{head}})", { group: lab(x, group), head: x.count(n, "{{count}} head", "{{count}} head") })
      : x.t("{{count}} of {{group}}", { count: n, group: lab(x, group) });
  }
  if (n <= 4) return x.list(animals.map((a) => lab(x, a)));
  return x.count(n, "{{count}} head", "{{count}} head");
}

function toMoveLike(e: FacetEvent<MoveData>): MoveLike | null {
  const to = e.subjects.find((s) => s.role === "to")?.id;
  const on = dayOf(e.occurred_at, e.occurred_precision);
  if (!to || !on) return null;
  return { id: e.id, on, to, from: e.subjects.find((s) => s.role === "from")?.id ?? null, group_id: e.subjects.find((s) => s.role === "group")?.id ?? null };
}

const rainUnitOf = (q: Quantity | undefined): RainUnit => (q?.unit === "mm" ? "mm" : "in");
const dayCount = (n: number) => (n === 1 ? msg("{{count}} day", { count: n }) : msg("{{count}} days", { count: n }));

// ---------------------------------------------------------------- events

const move = defineEvent<MoveData>({
  type: "land.move",
  label: "Cattle move",
  description: "Cattle were moved from one place to another.",
  icon: "move",
  tone: "land",
  subjects: [
    { role: "animal", kind: "animal", label: "Cattle", many: true, help: "Every animal that moved. A whole group lists its members as they are right now." },
    { role: "to", kind: "place", label: "Moved to", required: true },
    { role: "from", kind: "place", label: "Moved from" },
    { role: "group", kind: "group", label: "Group" },
  ],
  fields: {
    reason: f.enum("Why", MOVE_REASONS, { level: "more", agent: "Only when said or plain from the words (weaning, doctoring, rotation). starting = a record of where cattle already are." }),
    whole_group: f.bool("Whole group", { level: "more", agent: "true when they moved the whole group. Then list every current member in the animal role and add the group role." }),
    head: f.int("Head counted", { level: "more", min: 1, max: 100000, help: "For cattle counted rather than named.", agent: "Only when they gave a count. When no animals are named, the count is all that moves." }),
    count_unit: f.enum("Counted as", { head: "Head", pairs: "Pairs" }, { level: "more", agent: "pairs when they counted cow-calf pairs." }),
    remaining: f.int("Left behind", { level: "more", min: 0, max: 100000, help: "Head that stayed where they were.", agent: "Head they said stayed behind, e.g. 'seven pairs stayed' is 7." }),
    note: f.longText("Note", { level: "more", agent: "Keep the speaker's words and language." }),
  },
  summary: (e, x) => {
    const d = e.data;
    const who = moveWho(d, e.subjects, x);
    const to = lab(x, role(e.subjects, "to"));
    const from = role(e.subjects, "from");
    const parts = [d.reason === "starting"
      ? x.t("{{who}} in {{place}}", { who, place: to })
      : from ? x.t("{{who}} from {{from}} to {{to}}", { who, from: lab(x, from), to }) : x.t("{{who}} to {{to}}", { who, to })];
    if (d.reason && d.reason !== "starting" && d.reason !== "rotation") parts.push(x.t(MOVE_REASONS[d.reason]));
    if (d.remaining) parts.push(x.count(d.remaining, "{{count}} left behind", "{{count}} left behind"));
    return parts.join(" · ");
  },
  agent: {
    when: "Cattle were actually moved, turned out or put somewhere (pasture, trap, pen, field). Put every animal that moved in the animal role; for a group, list its current members (find_animals with group_id or who_is_where) and add the group role with whole_group true. 'to' is the destination; add 'from' when they said it or when every animal was in the same place (find_animals shows location).",
    avoid: "Not for moves that haven't happened yet ('move them tomorrow', 'need to move'): make a task or a grazing plan step. Not for selling, shipping off the ranch or a death (herd). Turning a bull in with cows is a move here; any breeding exposure is a breeding record.",
    examples: ["moved the spring calvers to North", "put 42 and her calf in pen 1", "turned the bulls out in River Bottom", "moved 36 pairs from creek to south, left 84 and her calf"],
  },
});

const rainfall = defineEvent<RainfallData>({
  type: "land.rainfall",
  label: "Rain",
  description: "A rain gauge reading or reported rainfall.",
  icon: "rain",
  tone: "land",
  subjects: [
    { role: "place", kind: "place", label: "Gauge at", help: "Leave empty for the ranch's main gauge." },
    { role: "gauge", kind: "asset", label: "Gauge or station" },
  ],
  fields: {
    amount: f.quantity("Amount", "precipitation", { required: true, agent: "Convert fractions and words: 'an inch and a quarter' is 1.25 in, 'half inch' is 0.5 in. A reading of zero is allowed only when they said none fell." }),
    since: f.date("Since", { help: "When the gauge was last emptied, when the reading covers several days.", agent: "Only when they said since when ('since Tuesday'). Otherwise leave out." }),
    kind: f.enum("Fell as", RAIN_KIND, { level: "more" }),
    basis: f.enum("Measured by", RAIN_BASIS, { level: "more", agent: "gauge when they read a gauge; estimate when guessed; station for a weather station." }),
    snow_depth: f.quantity("Snow depth", "length", { level: "more" }),
    note: f.longText("Note", { level: "more" }),
  },
  summary: (e, x) => {
    // Rain keeps two decimals ("1.25 in"); the general quantity format would round it to one.
    const parts = [`${Number(e.data.amount.value.toFixed(e.data.amount.unit === "mm" ? 1 : 2))} ${UNIT_LABELS[e.data.amount.unit] ?? e.data.amount.unit}`];
    const place = role(e.subjects, "place") ?? role(e.subjects, "gauge");
    if (place) parts.push(x.t("at {{place}}", { place: lab(x, place) }));
    if (e.data.since) parts.push(x.t("since {{date}}", { date: x.date(e.data.since) }));
    let text = parts.join(" ");
    if (e.data.kind && e.data.kind !== "rain") text += ` · ${x.t(RAIN_KIND[e.data.kind])}`;
    if (e.data.basis === "estimate") text += ` · ${x.t("estimate")}`;
    return text;
  },
  agent: {
    when: "Someone read a rain gauge or reported how much rain, snow or hail fell.",
    avoid: "A forecast or 'it might rain' is nothing to record.",
    examples: ["got 1.2 inches at headquarters", "north gauge has an inch and a quarter since Tuesday", "half inch last night"],
  },
});

const forage = defineEvent<ForageData>({
  type: "land.forage",
  label: "Forage check",
  description: "How the grass looks in a pasture: height, use and condition.",
  icon: "grass",
  tone: "land",
  subjects: [{ role: "place", kind: "place", label: "Pasture", required: true }],
  fields: {
    height: f.quantity("Grass height", "length", { agent: "Average standing height as said." }),
    grazed: f.enum("How grazed", FORAGE_GRAZED),
    condition: f.enum("Condition", FORAGE_CONDITION),
    mass: f.quantity("Forage estimate", "any", { level: "more", units: FORAGE_UNITS, help: "Pounds per acre or kilograms per hectare of standing forage.", agent: "Only when they gave pounds per acre or kg per hectare." }),
    basis: f.enum("How it was measured", FORAGE_BASIS, { level: "more", agent: "eyeball when they looked and guessed; leave out when not said." }),
    species: f.text("Main forage", { level: "more" }),
    note: f.longText("Note", { level: "more", agent: "Keep the speaker's words and language." }),
  },
  summary: (e, x) => {
    const d = e.data;
    const parts: string[] = [];
    if (d.height) parts.push(x.t("{{height}} tall", { height: x.quantity(d.height) }));
    if (d.grazed) parts.push(x.t(FORAGE_GRAZED[d.grazed]));
    if (d.condition) parts.push(x.t(FORAGE_CONDITION[d.condition]));
    if (d.mass) parts.push(d.basis === "clipped" || d.basis === "lab" ? x.quantity(d.mass) : x.t("about {{amount}}", { amount: x.quantity(d.mass) }));
    if (!parts.length && d.note) parts.push(d.note);
    return parts.join(" · ") || x.t("Forage check");
  },
  validate: (d) => (d.height || d.grazed || d.condition || d.mass || d.note ? [] : [{ path: "data.height", message: "Say how the grass looks: height, how grazed or condition." }]),
  agent: {
    when: "Someone looked at grass or forage in a pasture: height, how grazed it is, condition or a pounds-per-acre estimate.",
    examples: ["grass in South is about 8 inches and hasn't been touched", "North is grazed down to 3 inches", "river bottom looks fair, maybe 1500 pounds an acre"],
  },
});

const hayHarvest = defineEvent<HayHarvestData>({
  type: "land.hay_harvest",
  label: "Hay baled",
  description: "Hay cut and baled off a field, added to hay on hand.",
  icon: "hay",
  tone: "land",
  review: "always",
  subjects: [{ role: "field", kind: "place", label: "Field", required: true }],
  fields: {
    bales: f.int("Bales", { required: true, min: 1, max: 100000 }),
    bale_type: f.enum("Bale type", BALE_TYPES, { agent: "rounds = round." }),
    cutting: f.int("Cutting", { min: 1, max: 12, help: "First, second or later cutting this year." }),
    bale_weight: f.quantity("Average bale weight", "weight"),
    weight_basis: f.enum("Bale weight is", WEIGHT_BASIS, { level: "more", agent: "estimated when they said about or around; weighed only when bales were weighed." }),
    moisture: f.number("Moisture", { level: "more", min: 0, max: 100, suffix: "%" }),
    acres: f.quantity("Acres cut", "area", { level: "more", help: "The ground actually cut, for yield.", agent: "Only when said." }),
    forage: f.text("Hay type", { level: "more", placeholder: "Coastal, alfalfa, haygrazer…" }),
    stock_item_id: f.ref("Goes into", "stock_item", { level: "more", filter: { kind: "hay" }, agent: "The hay product the bales are counted under. Look it up with find_records kind stock_item." }),
    own_lot: f.bool("Keep as their own lot", { level: "more", help: "So feeding and sales draw down these bales.", agent: "true when the bales go into hay on hand, unless they named an existing lot." }),
    lot_code: f.text("Lot name", { level: "more", help: "Leave empty to name it from the field and cutting." }),
    lot_id: f.ref("Add to lot", "stock_lot", { level: "more", agent: "Only when they named an existing lot to add the bales to." }),
    quality: f.object("Hay test", {
      dry_matter: f.number("Dry matter", { min: 0, max: 100, suffix: "%" }),
      crude_protein: f.number("Crude protein", { min: 0, max: 100, suffix: "%" }),
      adf: f.number("ADF", { min: 0, max: 100, suffix: "%" }),
      ndf: f.number("NDF", { min: 0, max: 100, suffix: "%" }),
      rfv: f.number("Relative feed value", { min: 0, max: 400, help: "An index, not a nutrient." }),
    }, { level: "more" }),
    note: f.longText("Note", { level: "more" }),
  },
  summary: (e, x) => {
    const d = e.data;
    const bales = d.bale_type === "small_square" ? x.count(d.bales, "{{count}} small square bale", "{{count}} small square bales")
      : d.bale_type === "large_square" ? x.count(d.bales, "{{count}} big square bale", "{{count}} big square bales")
      : d.bale_type === "round" ? x.count(d.bales, "{{count}} round bale", "{{count}} round bales")
      : x.count(d.bales, "{{count}} bale", "{{count}} bales");
    const parts = [bales];
    if (d.cutting) parts.push(x.t("cutting {{count}}", { count: d.cutting }));
    if (d.bale_weight) parts.push(d.weight_basis === "weighed" ? x.t("{{weight}} each", { weight: x.quantity(d.bale_weight) }) : x.t("about {{weight}} each", { weight: x.quantity(d.bale_weight) }));
    if (d.moisture !== undefined) parts.push(x.t("{{value}}% moisture", { value: d.moisture }));
    return parts.join(" · ");
  },
  effects: {
    stock: (d) => (d.stock_item_id || d.lot_id) && d.bales > 0
      ? [{ ...(d.lot_id ? { lot_id: d.lot_id } : {}), ...(d.stock_item_id ? { item_id: d.stock_item_id } : {}), delta: d.bales, unit: "bale", reason: "receive" }]
      : [],
  },
  agent: {
    when: "Hay was cut and baled off a field and counted: bales, cutting, bale size or weight.",
    avoid: "Not for buying hay (a stock receipt), feeding hay (feeding) or mowing that wasn't baled (field work).",
    examples: ["baled 140 rounds off the east field", "second cutting made 84 round bales, about 1200 pounds each"],
  },
});

const fieldWork = defineEvent<FieldWorkData>({
  type: "land.field_work",
  label: "Field work",
  description: "Spraying, fertilizing, planting, burning, mowing or other work on a field or pasture.",
  icon: "tractor",
  tone: "land",
  review: "always",
  subjects: [
    { role: "field", kind: "place", label: "Field or pasture", required: true },
    { role: "equipment", kind: "asset", label: "Equipment" },
  ],
  fields: {
    work: f.enum("Work", FIELD_WORK, { required: true }),
    product_id: f.ref("Product", "stock_item", { agent: "The chemical, fertilizer or seed used. Look it up with find_records kind stock_item." }),
    rate: f.quantity("Rate", "any", { units: RATE_UNITS, agent: "Product per acre or hectare as said, e.g. 1 qt/ac." }),
    acres: f.quantity("Area covered", "area"),
    used: f.quantity("Total product used", "any", { level: "more", help: "In the unit the product is counted in. Takes it off what's on hand.", agent: "Only when said, in the product's counted unit." }),
    target: f.text("For", { level: "more", placeholder: "Mesquite, weeds, armyworms…" }),
    keep_off_days: f.int("Keep cattle off (days)", { level: "more", min: 0, max: 365, help: "From the product label. Hondo reminds you when cattle can go back." }),
    done_by: f.text("Done by", { level: "more", help: "Who did the work, if not the crew." }),
    note: f.longText("Note", { level: "more" }),
  },
  summary: (e, x) => {
    const d = e.data;
    const parts = [x.t(FIELD_WORK[d.work])];
    if (d.product_id) parts.push(x.label(d.product_id));
    if (d.rate) parts.push(x.quantity(d.rate));
    if (d.acres) parts.push(x.quantity(d.acres));
    if (d.target) parts.push(x.t("for {{target}}", { target: d.target }));
    return parts.join(" · ");
  },
  validate: (d) => (d.used && !d.product_id ? [{ path: "data.product_id", message: "Pick the product that was used." }] : []),
  effects: {
    stock: (d) => (d.product_id && d.used && d.used.value > 0 ? [{ item_id: d.product_id, delta: -d.used.value, unit: d.used.unit, reason: "use" }] : []),
    followUps: (d, on, subjects) => {
      const field = subjects.find((s) => s.role === "field");
      return d.keep_off_days && on && field
        ? [{ key: "keep_off", title: msg("Cattle can go back on the treated ground"), due_at: addDays(on, d.keep_off_days), subject: { kind: "place", id: field.id } }]
        : [];
    },
  },
  agent: {
    when: "Work done on a field or pasture: spraying, fertilizing, planting, burning, mowing, tilling or irrigating.",
    avoid: "Not for baling hay (hay baled) or fixing a fence, pump or windmill (equipment repair). Work that's still to do is a task.",
    examples: ["sprayed the south pasture for mesquite at a quart of Remedy per acre", "put 100 pounds of urea on the hay field", "burned river bottom today"],
  },
});

const waterCheck = defineEvent<WaterCheckData>({
  type: "land.water_check",
  label: "Water check",
  description: "How the water was at a tank, trough, windmill, well or pond.",
  icon: "water",
  tone: "land",
  subjects: [
    { role: "place", kind: "place", label: "Place" },
    { role: "water", kind: "asset", label: "Water source", help: "The windmill, tank or trough, when it's in your equipment list." },
  ],
  fields: {
    state: f.enum("Water", WATER_STATES, { required: true, agent: "ok for full, fine or running; low; empty or dry; broken when the windmill, pump or float isn't working." }),
    source: f.enum("What was checked", WATER_SOURCES, { level: "more" }),
    level: f.int("Level", { level: "more", min: 0, max: 100, suffix: "%" }),
    note: f.longText("Note", { level: "more", agent: "Keep the speaker's words and language." }),
  },
  summary: (e, x) => {
    const what = role(e.subjects, "water") ?? role(e.subjects, "place");
    const state = x.t(WATER_STATES[e.data.state]);
    const text = what ? x.t("{{what}}: {{state}}", { what: lab(x, what), state }) : state;
    return e.data.level !== undefined ? `${text} · ${e.data.level}%` : text;
  },
  agent: {
    when: "Someone checked a tank, trough, windmill, well or pond and said how the water was.",
    avoid: "A repair to a windmill or pump is an equipment repair (operations).",
    examples: ["the north windmill tank is low", "trough in south is empty", "checked water in River Bottom, all full"],
  },
});

// ---------------------------------------------------------------- facets

const location = defineFacet<LocationState>({
  id: "location",
  subject: "animal",
  version: 1,
  eventTypes: ["land.move"],
  reduce(events) {
    const moves = events.filter((e) => e.roles.includes("animal")).map((e) => ({ e, m: toMoveLike(e as FacetEvent<MoveData>) })).filter((x) => x.m);
    if (!moves.length) return null;
    const stays = staysFromMoves(moves.map((x) => x.m!));
    const current = stays.at(-1)!;
    const started = moves.find((x) => x.e.id === current.event_id)!;
    return {
      place_id: current.place_id,
      since: current.from,
      precision: started.e.occurred_precision,
      event_id: current.event_id,
      ...(started.m!.from ? { from_place_id: started.m!.from } : {}),
      ...(current.group_id ? { group_id: current.group_id } : {}),
      moves: moves.length,
      history: stays.slice(-40),
    };
  },
  // No move events: leave whatever location the animal already has (imports, setup).
  project: (s) => (s ? { location_id: s.place_id, location_since: s.since } : {}),
});

const placeLand = defineFacet<PlaceLandState>({
  id: "land",
  subject: "place",
  version: 1,
  eventTypes: ["land.rainfall", "land.forage", "land.hay_harvest", "land.field_work", "land.water_check", "land.move"],
  reduce(events) {
    const s: PlaceLandState = {};
    for (const e of events) {
      const on = dayOf(e.occurred_at, e.occurred_precision);
      if (!on) continue;
      switch (e.type) {
        case "land.rainfall": {
          if (!e.roles.includes("place")) break;
          const d = e.data as RainfallData;
          const unit = s.rain?.unit ?? rainUnitOf(d.amount);
          const amount = rainIn(d.amount, unit);
          if (amount === null) break;
          const month = on.slice(0, 7);
          const rain = s.rain ?? { unit, months: {}, last: { on, amount }, readings: 0 };
          rain.months[month] = roundRain((rain.months[month] ?? 0) + amount, unit);
          rain.last = { on, amount: roundRain(amount, unit), ...(d.since ? { since: d.since } : {}) };
          rain.readings++;
          s.rain = rain;
          break;
        }
        case "land.forage": {
          const d = e.data as ForageData;
          s.forage = { on, event_id: e.id, ...(d.height ? { height: d.height } : {}), ...(d.condition ? { condition: d.condition } : {}), ...(d.grazed ? { grazed: d.grazed } : {}),
            ...(d.mass ? { mass: d.mass } : {}), ...(d.basis ? { basis: d.basis } : {}) };
          break;
        }
        case "land.hay_harvest": {
          const d = e.data as HayHarvestData;
          const year = on.slice(0, 4);
          const hay = s.hay ?? { years: {}, last: { on, bales: d.bales, event_id: e.id } };
          const y = hay.years[year] ?? { bales: 0, cuttings: 0 };
          hay.years[year] = { bales: y.bales + d.bales, cuttings: y.cuttings + 1 };
          hay.last = { on, bales: d.bales, event_id: e.id, ...(d.bale_type ? { bale_type: d.bale_type } : {}), ...(d.cutting ? { cutting: d.cutting } : {}) };
          s.hay = hay;
          break;
        }
        case "land.field_work": {
          const d = e.data as FieldWorkData;
          const offUntil = d.keep_off_days ? addDays(on, d.keep_off_days) : undefined;
          const keep = s.work?.off_until && (!offUntil || s.work.off_until > offUntil) ? s.work.off_until : offUntil;
          s.work = { last: { on, work: d.work, event_id: e.id }, ...(keep ? { off_until: keep } : {}) };
          break;
        }
        case "land.water_check": {
          if (!e.roles.includes("place")) break;
          const d = e.data as WaterCheckData;
          const key = e.subjects.find((x) => x.role === "water")?.id ?? "place";
          // "Checked water in North, all full" covers every source there; a named source only covers itself.
          const sources = key === "place" && d.state === "ok" ? { place: { on, state: d.state } } : { ...(s.water?.sources ?? {}), [key]: { on, state: d.state } };
          const worst = (Object.values(sources).sort((a, b) => WATER_SEVERITY[b.state] - WATER_SEVERITY[a.state])[0]?.state ?? d.state) as WaterState;
          s.water = { on, state: d.state, worst, sources };
          break;
        }
        case "land.move": {
          const d = e.data as MoveData;
          if (e.subjects.some((x) => x.role === "animal") || !d.head) break;
          const unit = d.count_unit === "pairs" ? "pairs" : "head";
          const cur = s.counted && s.counted.unit === unit ? s.counted.head : 0;
          if (e.roles.includes("to")) s.counted = { head: cur + d.head, unit, since: on };
          else if (e.roles.includes("from")) {
            const left = Math.max(0, cur - d.head);
            if (left > 0 && s.counted) s.counted = { ...s.counted, head: left };
            else delete s.counted;
          }
          break;
        }
      }
    }
    return Object.keys(s).length ? s : null;
  },
  badges(s, { today }) {
    const out: Badge[] = [];
    const worst = s.water?.worst;
    if (worst === "low") out.push({ label: msg("Water low"), tone: "warn", icon: "water" });
    if (worst === "empty") out.push({ label: msg("Water empty"), tone: "bad", icon: "water" });
    if (worst === "broken") out.push({ label: msg("Water not working"), tone: "bad", icon: "water" });
    if (s.work?.off_until && s.work.off_until > today) {
      const n = daysBetween(today, s.work.off_until);
      out.push({ label: n === 1 ? msg("Keep cattle off {{count}} more day", { count: n }) : msg("Keep cattle off {{count}} more days", { count: n }), tone: "warn", icon: "warn" });
    }
    return out;
  },
  facts(s, { today }) {
    const out: Fact[] = [];
    if (s.rain) {
      const month = today.slice(0, 7);
      const year = today.slice(0, 4);
      const ytd = Object.entries(s.rain.months).filter(([m]) => m.startsWith(year)).reduce((n, [, v]) => n + v, 0);
      out.push({ label: "Rain this month", value: msg("{{amount}} {{unit}}", { amount: roundRain(s.rain.months[month] ?? 0, s.rain.unit), unit: s.rain.unit }), icon: "rain", tone: "land" });
      out.push({ label: "Rain this year", value: msg("{{amount}} {{unit}}", { amount: roundRain(ytd, s.rain.unit), unit: s.rain.unit }), icon: "rain" });
    }
    if (s.forage) {
      const days = daysBetween(s.forage.on, today);
      const height = s.forage.height ? `${s.forage.height.value} ${s.forage.height.unit}` : null;
      const value = height
        ? days <= 0 ? msg("{{height}}, today", { height }) : days === 1 ? msg("{{height}}, yesterday", { height }) : msg("{{height}}, {{count}} days ago", { height, count: days })
        : days <= 0 ? msg("Checked today") : days === 1 ? msg("Checked yesterday") : msg("Checked {{count}} days ago", { count: days });
      out.push({ label: "Grass", value, icon: "grass" });
    }
    const hay = s.hay?.years[today.slice(0, 4)];
    if (hay) out.push({ label: "Hay this year", value: hay.bales === 1 ? msg("{{count}} bale", { count: 1 }) : msg("{{count}} bales", { count: hay.bales }), icon: "hay" });
    return out;
  },
});

const grazing = defineFacet<GrazingState>({
  id: "grazing",
  subject: "place",
  version: 1,
  // Written by the land server from the stays ledger after every move change; see server/ledger.ts.
  eventTypes: [],
  reduce: () => null,
  badges(s, { today }) {
    const out: Badge[] = [];
    if (s.head > 0 && s.capacity && s.head > s.capacity) out.push({ label: msg("Over capacity"), tone: "warn", icon: "warn" });
    if (s.head > 0 && s.occupied_since) {
      const n = daysBetween(s.occupied_since, today);
      out.push({ label: n === 1 ? msg("In use {{count}} day", { count: n }) : msg("In use {{count}} days", { count: n }), tone: "land", icon: "animal" });
    }
    const rest = s.head === 0 && s.grazed ? restDays(s.empty_since, today) : null;
    if (rest !== null) {
      out.push({ label: rest === 1 ? msg("Rested {{count}} day", { count: rest }) : msg("Rested {{count}} days", { count: rest }), tone: "land", icon: "grass" });
      if (s.rest_target && rest >= s.rest_target) out.push({ label: msg("Over rest target"), tone: "good", icon: "done" });
    }
    return out;
  },
  facts(s, { today }) {
    const out: Fact[] = [];
    if (s.head > 0) out.push({ label: "In use", value: s.occupied_since ? dayCount(daysBetween(s.occupied_since, today)) : msg("Now"), icon: "animal", tone: "land" });
    else if (s.grazed) {
      const rest = restDays(s.empty_since, today);
      if (rest !== null) out.push({ label: "Rested", value: s.rest_target ? msg("{{count}} of {{target}} days", { count: rest, target: s.rest_target }) : dayCount(rest), icon: "grass", tone: s.rest_target && rest >= s.rest_target ? "good" : "land" });
    }
    return out;
  },
});

const assetLand = defineFacet<AssetLandState>({
  id: "land",
  subject: "asset",
  version: 1,
  eventTypes: ["land.water_check"],
  reduce(events) {
    const last = [...events].reverse().find((e) => e.roles.includes("water"));
    if (!last) return null;
    const d = last.data as WaterCheckData;
    return { water: { on: dayOf(last.occurred_at, last.occurred_precision) ?? last.recorded_at.slice(0, 10), state: d.state, ...(d.level !== undefined ? { level: d.level } : {}) } };
  },
  badges(s) {
    if (s.water.state === "low") return [{ label: msg("Water low"), tone: "warn", icon: "water" }];
    if (s.water.state === "empty") return [{ label: msg("Water empty"), tone: "bad", icon: "water" }];
    if (s.water.state === "broken") return [{ label: msg("Water not working"), tone: "bad", icon: "water" }];
    return [];
  },
});

// ---------------------------------------------------------------- module

export default defineModule({
  id: "land",
  label: "Land and grazing",
  description: "Pastures, moves, grazing plans, forage, hay, rain and field work.",
  events: [move, rainfall, forage, hayHarvest, fieldWork, waterCheck],
  facets: [location, placeLand, grazing, assetLand],
  jobs: [{
    type: "land.grazing_plan",
    label: "Grazing plan",
    description: "Where a group goes next, for how long, and how much rest each pasture gets.",
    icon: "route",
    tone: "land",
    workspace: "grazing",
    fields: {
      group_id: f.ref("Which cattle", "group"),
      head: f.int("Head", { level: "more", min: 1, help: "When the cattle aren't a group, or to plan for a different count." }),
      rest_target_days: f.int("Rest target (days)", { min: 0, max: 730, help: "The least rest each pasture should get before cattle come back." }),
      au_per_head: f.number("Animal units per head", { level: "more", min: 0.1, max: 3, help: "Leave empty and Hondo estimates from each animal's class." }),
      intake_lb: f.number("Daily forage per animal unit (lb)", { level: "more", min: 1, max: 100, help: "26 lb of dry forage is a common figure for a 1,000 lb cow." }),
      utilization_pct: f.int("Planned use of standing forage", { level: "more", min: 1, max: 100, suffix: "%", help: "Take half, leave half is 50%." }),
      residual: f.quantity("Leave at least", "any", { level: "more", units: FORAGE_UNITS, help: "Forage to leave standing. Used instead of the planned use when set." }),
      note: f.longText("Note", { level: "more" }),
    },
    agent: {
      when: "Someone wants to plan where a group grazes next (a rotation): 'give the south cows three days in trap two, then creek, keep thirty days of rest'. data: { group_id, rest_target_days, stages: [{ place_id, start: YYYY-MM-DD, days }] } in order. A plan never moves cattle; actual moves are land.move events.",
    },
  }],
  workspaces: [{
    id: "grazing",
    label: "Plan and record grazing",
    description: "Who's where, moves, rotation plans, pasture rest, rain and hay.",
    icon: "grass",
    group: "operations",
    keywords: ["move", "moves", "moving", "pasture", "pastures", "grazing", "rotation", "rest", "forage", "grass", "rain", "rainfall", "gauge", "hay", "bales", "baling", "field work", "spray", "fertilize", "water", "windmill", "tank", "trough", "map", "who's where"],
    keywordsEs: ["mover", "movimiento", "potrero", "potreros", "pastoreo", "rotación", "descanso", "forraje", "pasto", "zacate", "lluvia", "pluviómetro", "pacas", "heno", "empacar", "fumigar", "fertilizar", "agua", "papalote", "tanque", "bebedero", "pila", "mapa"],
    presets: ["basic", "commercial"],
  }],
  ranchSections: [{
    id: "map",
    label: "Ranch map",
    description: "Pastures on the map with head counts, rest and water.",
    icon: "map",
    group: "places",
    keywords: ["map", "boundaries", "pasture map", "acres"],
    keywordsEs: ["mapa", "linderos", "potreros", "hectáreas"],
  }],
  attributes: {
    place: {
      rest_target_days: f.int("Rest target (days)", { level: "more", min: 0, max: 730, help: "How long this pasture should rest before cattle go back in." }),
      usable_area: f.quantity("Grazable area", "area", { level: "more", help: "Leave out ponds, timber and ground cattle can't use." }),
      forage_type: f.text("Main forage", { level: "more" }),
      premises_id: f.text("Premises ID", { level: "more", help: "An official premises or location ID, kept exactly as written." }),
    },
    asset: {
      lat: f.number("Latitude", { level: "more", min: -90, max: 90 }),
      lng: f.number("Longitude", { level: "more", min: -180, max: 180 }),
    },
  },
  settings: {
    rest_target_days: f.int("Rest target for pastures (days)", { min: 0, max: 730, help: "Used for any pasture without its own target." }),
    utilization_pct: f.int("Planned use of standing forage", { min: 1, max: 100, suffix: "%", help: "Take half, leave half is 50%." }),
    intake_lb: f.number("Daily forage per animal unit (lb)", { min: 1, max: 100, help: "26 lb of dry forage is a common figure for a 1,000 lb cow." }),
    main_gauge: f.ref("Main rain gauge", "place", { help: "Where rain is counted for the ranch when a reading names no place." }),
  },
  messages: [
    // Facet badges and facts
    "Water low", "Water empty", "Water not working", "Keep cattle off {{count}} more days", "Rain this month", "Rain this year", "{{amount}} {{unit}}",
    "Grass", "{{height}}, today", "{{height}}, yesterday", "{{height}}, {{count}} days ago", "Checked today", "Checked yesterday", "Checked {{count}} days ago",
    "Hay this year", "{{count}} bale", "{{count}} bales", "Over capacity", "In use {{count}} day", "In use {{count}} days", "Rested {{count}} day", "Rested {{count}} days", "Over rest target",
    "In use", "Now", "{{count}} day", "{{count}} days", "Keep cattle off {{count}} more day",
    "Rested", "{{count}} of {{target}} days",
    // Web labels chosen at runtime (sections, plan step states, estimate notes, map checks)
    "Where it's been", "Grazing", "Water", "Planned", "Move due", "Grazing now", "Done", "Not moved", "Step skipped",
    "Add a forage check with a pounds-per-acre estimate to see how long the grass will last.",
    "Add the pasture's acres, or draw it on the map, to see how long the grass will last.",
    "No cattle to plan for.", "There's no forage above what you want to leave standing.", "That forage estimate is in units Hondo can't use for a budget.",
    "Tap at least three corners.", "A corner is off the map.", "The outline crosses itself. Drag a corner to untangle it.",
    "from the plan's figure per head", "estimated from each animal's class", "one per head, assumed", "Grazable area", "Mapped area", "Area",
    // Validation
    "That date hasn't come yet. A move that's still to happen goes in a grazing plan or a task.",
    "That date hasn't come yet.",
    "Give a date, even a rough one, so Hondo can keep where cattle were in order.",
    "They're already headed there. Pick a different place.",
    "List the cattle that moved, or how many head.",
    "Pick the group that moved.",
    "A reference animal can't be moved.",
    "One of these animals had already left the herd by then.",
    "Rain at this gauge was already recorded for some of these days. Correct that reading instead.",
    "The start of the reading has to be before the reading.",
    "Pick a hay product counted in bales.", "Pick the hay product these bales go into.",
    "That lot belongs to a different product.",
    "Record the amount in the unit the product is counted in.",
    "Say which place or water source was checked.",
    "Pick a place.",
    "Every plan step needs a pasture, a start date and how many days.",
    // Today
    "Move {{group}} to {{place}}", "Planned move to {{place}}", "Rested {{rest}} of {{target}} days", "{{place}} has {{head}} head; it holds {{capacity}}",
    "{{count}} pastures rested past their target", "{{place}} rested past its target", "{{amount}} {{unit}} of rain in the last week",
    "{{what}}: water low", "{{what}}: water empty", "{{what}}: water not working",
  ],
});

export const LAND_EVENT_TYPES = ["land.move", "land.rainfall", "land.forage", "land.hay_harvest", "land.field_work", "land.water_check"] as const;
